import React, { useState, useCallback } from 'react';
import { Box, Chip, Typography, IconButton, Tooltip } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import LocationCityIcon from '@mui/icons-material/LocationCity';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import ToggleOffIcon from '@mui/icons-material/ToggleOff';
import { GridRenderCellParams, GridRowId } from '@mui/x-data-grid';

import PaginatedDataGrid, {
  PaginatedDataGridColumn,
  PaginatedDataGridAction
} from 'src/components/paginated-data-grid/PaginatedDataGrid';

import SimpleConfirmationDialog from 'src/components/SimpleConfirmationDialog';
import CityDialog from './CityDialog';
import { DataService } from 'src/services/data-service';
import { useAppDispatch } from 'src/redux/hooks';
import { showAlert } from 'src/redux/alert/alertReducer';

interface City {
  id: string;
  name: string;
  active: boolean;
  createdAt: string;
  updatedAt: string;
  country?: {
    id: string;
    name: string;
    code: string;
  } | null;
}

const CitiesListing: React.FC = () => {
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedCity, setSelectedCity] = useState<City | null>(null);
  const [toggleLoading, setToggleLoading] = useState<string | null>(null);
  const dispatch = useAppDispatch();

  // ---------- Delete City ----------
  const handleDeleteClick = (id: GridRowId) => {
    setDeleteId(String(id));
  };

  const handleDeleteConfirm = async () => {
    if (!deleteId) return;

    try {
      const response = await DataService.delete(`api/City/${deleteId}`, {});
      if (response.ok) {
        setRefreshKey((prev) => prev + 1);
        dispatch(
          showAlert({
            message: 'City deleted successfully!',
            type: 'success'
          })
        );
      } else {
        throw new Error('Failed to delete city');
      }
    } catch (error) {
      dispatch(
        showAlert({
          message: 'Failed to delete city. Please try again.',
          type: 'error'
        })
      );
    } finally {
      setDeleteId(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteId(null);
  };

  // ---------- Edit City ----------
  const handleEditClick = (city: City) => {
    setSelectedCity(city);
    setEditDialogOpen(true);
  };

  // ---------- Add City ----------
  const handleAddCity = () => {
    setSelectedCity(null);
    setEditDialogOpen(true);
  };

  // ---------- Toggle Active/Inactive ----------
  const handleToggleActive = async (city: City) => {
    setToggleLoading(city.id);

    try {
      const endpoint = city.active
        ? `api/City/deactivate?id=${city.id}`
        : `api/City/activate?id=${city.id}`;

      const response = await DataService.patch(endpoint, {});

      if (response.ok) {
        setRefreshKey((prev) => prev + 1);
        dispatch(
          showAlert({
            message: `City ${city.active ? 'deactivated' : 'activated'} successfully!`,
            type: 'success'
          })
        );
      } else {
        throw new Error('Failed to update city status');
      }
    } catch (error) {
      dispatch(
        showAlert({
          message: 'Failed to update city status. Please try again.',
          type: 'error'
        })
      );
    } finally {
      setToggleLoading(null);
    }
  };

  // ---------- Handle Dialog Success ----------
  const handleDialogSuccess = () => {
    setEditDialogOpen(false);
    setRefreshKey((prev) => prev + 1);
    dispatch(
      showAlert({
        message: selectedCity ? 'City updated successfully!' : 'City created successfully!',
        type: 'success'
      })
    );
  };

  // ---------- Data Grid Columns ----------
  const columns: PaginatedDataGridColumn[] = [
    {
      field: 'name',
      headerName: 'City Name',
      flex: 1.5,
      minWidth: 180,
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LocationCityIcon color="primary" fontSize="small" />
          <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
            {params.value}
          </Typography>
        </Box>
      )
    },
    {
      field: 'country',
      headerName: 'Country',
      flex: 1.2,
      minWidth: 150,
      renderCell: (params: GridRenderCellParams) => (
        params.row.country ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
              {params.row.country.name}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                fontFamily: 'monospace',
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                px: 0.5,
                py: 0.25,
                borderRadius: 0.5,
                color: 'text.secondary'
              }}
            >
              {params.row.country.code}
            </Typography>
          </Box>
        ) : (
          <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
            No country assigned
          </Typography>
        )
      )
    },
    {
      field: 'active',
      headerName: 'Status',
      width: 120,
      renderCell: (params: GridRenderCellParams) => (
        <Chip
          label={params.value ? 'Active' : 'Inactive'}
          color={params.value ? 'success' : 'default'}
          size="small"
          variant="outlined"
        />
      )
    },
    {
      field: 'createdAt',
      headerName: 'Created',
      width: 130,
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {new Date(params.value).toLocaleDateString()}
        </Typography>
      )
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Tooltip title={params.row.active ? 'Deactivate' : 'Activate'}>
            <IconButton
              size="small"
              onClick={() => handleToggleActive(params.row)}
              disabled={toggleLoading === params.row.id}
              color={params.row.active ? 'success' : 'default'}
            >
              {params.row.active ? <ToggleOnIcon /> : <ToggleOffIcon />}
            </IconButton>
          </Tooltip>
          <Tooltip title="Edit">
            <IconButton
              size="small"
              onClick={() => handleEditClick(params.row)}
              color="primary"
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton
              size="small"
              onClick={() => handleDeleteClick(params.row.id)}
              color="error"
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      )
    }
  ];

  // ---------- Data Grid Actions (Toolbar) ----------
  const actions: PaginatedDataGridAction[] = [
    {
      key: 'add',
      title: 'Add City',
      icon: <AddIcon />,
      onClick: handleAddCity,
      disabled: () => false,
      color: 'primary'
    }
  ];

  // ---------- Fetch Cities Function ----------
  const fetchCities = useCallback(
    async (params: {
      page: number;
      pageSize: number;
      sortModel?: any;
      filterModel?: any;
      search?: string;
    }) => {
      console.log('fetchCities called with params:', params);
      try {
        const response = await DataService.get('api/City/all');

        if (response.ok) {
          const responseData = await response.json();
          console.log('API Response:', responseData);

          let rawCities: any[] = [];

          if (responseData.success && responseData.data && Array.isArray(responseData.data)) {
            rawCities = responseData.data;
          } else if (Array.isArray(responseData)) {
            rawCities = responseData;
          } else {
            console.error('Unexpected response structure:', responseData);
            throw new Error('Invalid response structure');
          }

          const cities: City[] = rawCities.map(city => ({
            id: city.id,
            name: city.name,
            active: city.active,
            createdAt: city.createdAt || new Date().toISOString(),
            updatedAt: city.updatedAt || new Date().toISOString(),
            country: city.country
          }));

          console.log('Raw cities from API:', rawCities);
          console.log('Mapped cities:', cities);

          // Filter by search term if provided
          let filteredCities = cities;
          if (params.search) {
            const searchLower = params.search.toLowerCase();
            filteredCities = cities.filter(
              city =>
                city.name.toLowerCase().includes(searchLower) ||
                (city.country?.name.toLowerCase().includes(searchLower))
            );
          }

          // Apply sorting if provided
          if (params.sortModel && params.sortModel.length > 0) {
            const sortModel = params.sortModel[0];
            filteredCities.sort((a, b) => {
              let aValue: any = a[sortModel.field as keyof City];
              let bValue: any = b[sortModel.field as keyof City];

              // Handle nested object properties (country)
              if (sortModel.field === 'country') {
                aValue = a.country?.name || '';
                bValue = b.country?.name || '';
              }

              // Handle string comparison
              if (typeof aValue === 'string') {
                aValue = aValue.toLowerCase();
                bValue = (bValue as string).toLowerCase();
              }

              if (sortModel.sort === 'desc') {
                return aValue < bValue ? 1 : -1;
              }
              return aValue > bValue ? 1 : -1;
            });
          }

          // Apply pagination (DataGrid uses 0-based page indexing)
          const startIndex = params.page * params.pageSize;
          const paginatedCities = filteredCities.slice(startIndex, startIndex + params.pageSize);

          const totalPages = Math.ceil(filteredCities.length / params.pageSize);

          const result = {
            items: paginatedCities,
            totalCount: filteredCities.length,
            pageNumber: params.page + 1,
            pageSize: params.pageSize,
            totalPages: totalPages,
            hasNext: params.page + 1 < totalPages,
            hasPrevious: params.page > 0
          };

          console.log('Final result being returned:', result);
          return result;
        } else {
          throw new Error(`Failed to fetch cities: ${response.status} ${response.statusText}`);
        }
      } catch (error) {
        console.error('Error fetching cities:', error);
        const errorMessage = error instanceof Error ? error.message : 'Failed to load cities. Please try again.';
        dispatch(
          showAlert({
            message: errorMessage,
            type: 'error'
          })
        );
        return {
          items: [],
          totalCount: 0,
          pageNumber: params.page + 1,
          pageSize: params.pageSize,
          totalPages: 0,
          hasNext: false,
          hasPrevious: false
        };
      }
    },
    [dispatch]
  );

  return (
    <Box sx={{ width: '100%', height: '100%' }}>
      <Typography variant="h5" sx={{ mb: 1.5, fontWeight: 'bold' }}>
        Cities Management
      </Typography>

      <PaginatedDataGrid
        key={refreshKey}
        title="Cities"
        columns={columns}
        useBackendPagination
        onDataFetch={fetchCities}
        actions={actions}
        emptyMessage="No cities found"
      />

      {/* Delete Confirmation Dialog */}
      <SimpleConfirmationDialog
        open={!!deleteId}
        title="Delete City"
        message="Are you sure you want to delete this city? This action cannot be undone."
        onConfirm={handleDeleteConfirm}
        onClose={handleDeleteCancel}
      />

      {/* Add/Edit City Dialog */}
      <CityDialog
        open={editDialogOpen}
        city={selectedCity}
        onClose={() => setEditDialogOpen(false)}
        onSuccess={handleDialogSuccess}
      />
    </Box>
  );
};

export default CitiesListing;